import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "template", "addButton", "removeButton"]
  static values = { 
    addressType: String, // Type of address being managed
    scrollOnAdd: { type: Boolean, default: true },
    animationDuration: { type: Number, default: 300 }
  }

  addAddress(event) {
    event.preventDefault()
    const button = event.currentTarget
    const addressType = button.dataset.addressType || this.addressTypeValue
    const container = this.element.querySelector(`.js-${addressType}-container`)
    const templateElement = this.element.querySelector(`.js-${addressType}-template`)
    
    if (!templateElement || !templateElement.content) {
      console.error('Template not found for address type:', addressType)
      return
    }
    
    if (!container) {
      console.error('Container not found for address type:', addressType)
      return
    }
    
    // Clone the template content
    const templateContent = templateElement.content.cloneNode(true)
    
    // Generate unique index using timestamp
    const newIndex = Date.now()
    
    // Convert to string, replace placeholders, then create element
    const tempDiv = document.createElement('div')
    tempDiv.appendChild(templateContent)
    const newAddressHtml = tempDiv.innerHTML.replace(/NEW_RECORD/g, newIndex)
    
    // Create new element and append
    const newAddressElement = document.createElement('div')
    newAddressElement.innerHTML = newAddressHtml
    const newAddressForm = newAddressElement.firstElementChild
    
    container.appendChild(newAddressForm)
    
    // Dispatch event for other controllers to hook into
    this.dispatchAddressAddedEvent(newAddressForm, addressType)
    
    // Stimulus will automatically initialize controllers on dynamically added elements
    
    // Scroll to new address form if enabled
    if (this.scrollOnAddValue) {
      this.scrollToElement(newAddressForm)
    }
  }

  removeAddress(event) {
    event.preventDefault()
    const button = event.currentTarget
    const addressForm = button.closest('.js-address-form')
    if (!addressForm) return
    
    // Check if this is a persisted address (has an ID field)
    const idField = addressForm.querySelector('input[name*="[id]"]')
    
    if (idField && idField.value) {
      // This is a persisted address - mark for destruction
      this.markForDestruction(addressForm, idField)
    } else {
      // Remove new addresses completely
      this.removeElement(addressForm)
    }
  }

  markForDestruction(addressForm, idField) {
    const destroyFieldName = idField.name.replace('[id]', '[_destroy]')
    let destroyField = addressForm.querySelector(`input[name="${destroyFieldName}"]`)
    
    if (!destroyField) {
      // Create the destroy field
      destroyField = document.createElement('input')
      destroyField.type = 'hidden'
      destroyField.name = destroyFieldName
      destroyField.value = '1'
      addressForm.appendChild(destroyField)
    } else {
      // Mark for destruction
      destroyField.value = '1'
    }
    
    // Hide the form with animation
    this.hideElement(addressForm)
  }

  removeElement(element) {
    this.fadeOut(element, () => {
      element.remove()
      this.dispatchAddressRemovedEvent(element)
    })
  }

  hideElement(element) {
    this.fadeOut(element, () => {
      element.classList.add('d-none')
      this.dispatchAddressRemovedEvent(element)
    })
  }

  fadeOut(element, callback) {
    element.style.transition = `opacity ${this.animationDurationValue}ms`
    element.style.opacity = '0'
    
    setTimeout(() => {
      if (callback) callback()
    }, this.animationDurationValue)
  }

  scrollToElement(element) {
    const offset = element.getBoundingClientRect().top + window.pageYOffset - 100
    
    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    })
  }

  dispatchAddressAddedEvent(addressForm, addressType) {
    const event = new CustomEvent('address:added', {
      bubbles: true,
      detail: { 
        addressForm, 
        addressType,
        controller: this
      }
    })
    this.element.dispatchEvent(event)
  }

  dispatchAddressRemovedEvent(addressForm) {
    const event = new CustomEvent('address:removed', {
      bubbles: true,
      detail: { 
        addressForm,
        controller: this
      }
    })
    this.element.dispatchEvent(event)
  }

  // Public methods for external use
  getActiveAddressForms(addressType = null) {
    const selector = addressType ? 
      `.js-${addressType}-container .js-address-form` : 
      '.js-address-form'
    
    const forms = this.element.querySelectorAll(selector)
    
    return Array.from(forms).filter(form => {
      // Exclude forms marked for deletion or hidden
      const destroyField = form.querySelector('input[name*="[_destroy]"]')
      const isDestroyed = destroyField && destroyField.value === '1'
      const isHidden = form.classList.contains('d-none')
      
      return !isDestroyed && !isHidden
    })
  }

  getAddressTypeContainers() {
    return this.element.querySelectorAll('[class*="js-"][class*="-container"]')
  }

  // Method to programmatically add an address
  addAddressOfType(addressType) {
    const addButton = this.element.querySelector(`.js-add-address[data-address-type="${addressType}"]`)
    if (addButton) {
      this.addAddress(addButton)
    }
  }
}